import { useCallback, useMemo } from 'react';

import { ConsumerStudentFee } from '@givebacks/api/student';
import { useForm } from '@givebacks/formy';
import { CheckoutButtons } from '@givebacks/shop';
import { centsToDollar } from '@givebacks/utils/currency';
import { CurrencyInput, Modal, ModalProps, Stack, Text } from 'ui';
import { isInRange } from 'ui/Form';

import { useFeeCartHandler } from '../../../hooks/useFeeCartHandler';

interface AddPartialPaymentModalProps extends ModalProps {
  studentFee: ConsumerStudentFee;
  /**
   * Store Item Id of the fee
   */
  storeItemId: string;

  /**
   * Initial amount to set in the form, should either be the remaining balance or the amount already in the cart
   */
  initialAmount?: number;

  /**
   * Existing cart info is used to provide the cartId and cartItemId to the useAddFeeToCartHandler hook
   * if both cartId and cartItemId are provided the modal will default to updating the cartItem
   */
  existingCartInfo?: {
    cartId?: string;
    cartItemId?: string;
  };

  /**
   * If true, the modal will be used to pay('buy now') now instead of adding to cart
   */
  isPayNow?: boolean;
}

interface AddPartialPaymentModalContentProps
  extends Pick<
    AddPartialPaymentModalProps,
    'studentFee' | 'storeItemId' | 'existingCartInfo' | 'initialAmount' | 'onClose' | 'isPayNow'
  > {}

type AddPartialPaymentModalFormValues = {
  amount: number;
};

const AddPartialPaymentModalContent = ({
  studentFee,
  storeItemId,
  initialAmount,
  existingCartInfo,
  onClose,
  isPayNow,
}: AddPartialPaymentModalContentProps) => {
  const { cartId, cartItemId } = existingCartInfo ?? {};

  const form = useForm<AddPartialPaymentModalFormValues>({
    initialValues: {
      amount: initialAmount ?? studentFee.amount_remaining,
    },
    validate: {
      amount: isInRange(
        { min: 1, max: studentFee.amount_remaining },
        'Amount must be greater than 1 and less than the remaining balance'
      ),
    },
  });

  const { handleUpdateExistingCartItem, cartItems, isLoading } = useFeeCartHandler({
    feeId: storeItemId,
    causeId: studentFee.cause_id,
    price: form.values.amount,
    studentFeeId: studentFee.uuid,
    cartHandlerOptions: {
      onCartCreate: onClose,
    },
  });

  const amountRemaining = useMemo(() => {
    return centsToDollar(studentFee.amount_remaining - form.values.amount);
  }, [form.values.amount, studentFee.amount_remaining]);

  const isUpdatingExistingCart = useMemo(() => {
    return cartId && cartItemId;
  }, [cartId, cartItemId]);

  const handleAddToCart = useCallback(() => {
    if (cartId && cartItemId) {
      if (form.validate().hasErrors) {
        return;
      }
      handleUpdateExistingCartItem({
        cartId,
        cartItemId,
      });
    }
  }, [cartId, cartItemId, form, handleUpdateExistingCartItem]);

  return (
    <form>
      <Stack>
        <CurrencyInput
          data-autofocus
          label="Enter Amount"
          placeholder="Amount"
          {...form.getInputProps('amount')}
        />
        <Text c="gray.6" fz="sm">
          Remaining Balance: {amountRemaining}
        </Text>

        <CheckoutButtons
          storeId={studentFee.cause_id}
          storeItemId={storeItemId}
          cartItems={cartItems}
          validateOnClick={true}
          onCartCreate={onClose}
          buyNowVisible={isPayNow}
          addToCartVisible={!isPayNow}
          buyNowProps={{
            buttonProps: {
              children: 'Pay Now',
            },
          }}
          addToCartProps={{
            onAddToCartClick: isUpdatingExistingCart ? handleAddToCart : undefined,
            buttonProps: {
              loading: isUpdatingExistingCart ? isLoading : undefined,
              variant: 'filled',
              children: 'Add to Cart',
            },
          }}
        />
      </Stack>
    </form>
  );
};

const AddPartialPaymentModal = ({
  studentFee,
  storeItemId,
  initialAmount,
  existingCartInfo,
  isPayNow = false,
  ...props
}: AddPartialPaymentModalProps) => {
  return (
    <Modal title="How much would you like to pay?" {...props}>
      <AddPartialPaymentModalContent
        studentFee={studentFee}
        storeItemId={storeItemId}
        initialAmount={initialAmount}
        existingCartInfo={existingCartInfo}
        onClose={props.onClose}
        isPayNow={isPayNow}
      />
    </Modal>
  );
};

export { AddPartialPaymentModal };
